import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { differenceInCalendarDays, formatDistanceToNowStrict } from "date-fns";
import { ArrowUpRight, Sparkle } from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "@/components/ui/ContentCard";
import { ResourceShelfSection } from "@/components/resources/ResourceShelf";
import { resourcesService } from "@/services/resourcesService";
import {
  conversationResourcesChangedEvent,
  fetchConversationStripeResources,
} from "@/services/conversationResourceService";
import { useAuth } from "@/hooks/useAuth";
import { extractFirstName } from "@/lib/userName";
import { useSession } from "@/hooks/useSession";
import { cn } from "@/lib/utils";
import {
  answeredInsightIds,
  recordMirrorInsightResponse,
  type MirrorInsightFeedback,
} from "@/services/sessionMirrorInsightService";
import { toast } from "sonner";
import type { Resource } from "@/types/resource";
import type {
  GrowthPattern,
  GrowthPatternAccent,
  LanguageShift,
  NarrativeEntry,
  NarrativeKind,
  SessionMirrorPrompt,
} from "@/config/insightsSample";
import { buildDerivedInsightsDashboard } from "@/services/conversationInsightsService";

const NARRATIVE_FILTERS: Array<{ id: NarrativeKind | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "reflection", label: "Reflections" },
  { id: "practice", label: "Practice" },
  { id: "milestone", label: "Milestones" },
];

const INSIGHTS_HISTORY_LIMIT = 60;

export const InsightsPage = () => {
  const location = useLocation();
  const { userData, isAuthenticated } = useAuth();
  const { closedSessions, loadClosedSessions } = useSession();

  const [generatedStripe, setGeneratedStripe] = useState<Resource[]>([]);
  const [userMirrorQueue, setUserMirrorQueue] = useState<SessionMirrorPrompt[]>([]);
  const [userGrowthPatterns, setUserGrowthPatterns] = useState<GrowthPattern[]>([]);
  const [userLanguageShifts, setUserLanguageShifts] = useState<LanguageShift[]>([]);
  const [userNarrativeEntries, setUserNarrativeEntries] = useState<NarrativeEntry[]>([]);
  const [sessionsWithMessages, setSessionsWithMessages] = useState(0);
  const [milestonesCount, setMilestonesCount] = useState(0);
  const [insightsTick, setInsightsTick] = useState(0);

  const loadGeneratedStripe = useCallback(async () => {
    if (!userData?.user_id) {
      setGeneratedStripe([]);
      return;
    }
    const next = await fetchConversationStripeResources();
    setGeneratedStripe(next);
  }, [userData?.user_id]);

  useEffect(() => {
    void loadGeneratedStripe();

    const onRefresh = () => {
      void loadGeneratedStripe();
    };
    window.addEventListener(conversationResourcesChangedEvent, onRefresh);

    return () => {
      window.removeEventListener(conversationResourcesChangedEvent, onRefresh);
    };
  }, [loadGeneratedStripe]);

  const [favoriteIds, setFavoriteIds] = useState<string[]>(
    () => resourcesService.getFavorites(userData?.user_id) || []
  );

  useEffect(() => {
    setFavoriteIds(resourcesService.getFavorites(userData?.user_id) || []);
  }, [userData?.user_id]);

  const toggleResourceFavorite = (id: string) => {
    if (!userData?.user_id) return;
    resourcesService.toggleUserFavorite(userData.user_id, id);
    setFavoriteIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  useEffect(() => {
    if (!isAuthenticated || location.pathname !== "/growth/insights") return;
    void loadClosedSessions(INSIGHTS_HISTORY_LIMIT);
  }, [isAuthenticated, location.pathname, loadClosedSessions]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => {
      if (document.visibilityState === "visible" && location.pathname === "/growth/insights") {
        void loadClosedSessions(INSIGHTS_HISTORY_LIMIT);
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [isAuthenticated, location.pathname, loadClosedSessions]);

  const userId = userData?.user_id;

  useEffect(() => {
    if (!userId || !closedSessions) return;

    let cancelled = false;
    const sessions = Object.values(closedSessions);

    void buildDerivedInsightsDashboard(sessions, userId).then((dashboard) => {
      if (cancelled) return;
      setUserMirrorQueue(dashboard.mirrorQueue);
      setUserGrowthPatterns(dashboard.patterns);
      setUserLanguageShifts(dashboard.languageShifts);
      setUserNarrativeEntries(dashboard.narrativeEntries);
      setSessionsWithMessages(dashboard.sessionsWithMessages);
      setMilestonesCount(dashboard.milestonesCount);
    });

    return () => {
      cancelled = true;
    };
  }, [userId, closedSessions, insightsTick]);

  const firstName = extractFirstName(userData) ?? "friend";

  const arrivedLabel = useMemo(() => {
    if (!userData?.created_at) return "—";
    const days = Math.max(0, differenceInCalendarDays(new Date(), new Date(userData.created_at)));
    if (days === 0) return "today";
    if (days === 1) return "1 day ago";
    if (days < 14) return `${days} days ago`;
    return formatDistanceToNowStrict(new Date(userData.created_at), { addSuffix: true });
  }, [userData?.created_at]);

  const sessionsCount = sessionsWithMessages;

  const onMirrorFeedbackRecorded = () => setInsightsTick((t) => t + 1);

  return (
    <Page.Container>
      <Page.Header className="space-y-2">
        <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">Insights</h1>
        <p className="max-w-2xl font-inter text-sm leading-relaxed text-white/80 md:text-base">
          A mirror, not a dashboard. What follows came from your own sessions and your own words.
        </p>
      </Page.Header>

      <Page.Content className="gap-0 space-y-6">
        <ArcHeroCard
          firstName={firstName}
          arrivedLabel={arrivedLabel}
          sessionsCount={sessionsCount}
          milestonesCount={milestonesCount}
          hasPersonalData={
            sessionsCount > 0 ||
            userMirrorQueue.length > 0 ||
            userGrowthPatterns.length > 0 ||
            generatedStripe.length > 0
          }
        />

        <HowYouAreGrowingSection
          userId={userData?.user_id ?? "__guest__"}
          mirrorQueue={userMirrorQueue}
          patterns={userGrowthPatterns}
          onMirrorFeedbackRecorded={onMirrorFeedbackRecorded}
        />

        {userLanguageShifts.length > 0 ? (
          <LanguageShiftsCard shifts={userLanguageShifts} />
        ) : (
          <InsightsEmptyCard
            title="How your language is shifting"
            body="When you write reflectively across sessions, HeyBodhi will surface changes in the words you reach for — drawn only from your own writing."
          />
        )}

        {userNarrativeEntries.length > 0 ? (
          <GrowthNarrativeCard entries={userNarrativeEntries} />
        ) : (
          <InsightsEmptyCard
            title="Your growth narrative"
            body="Reflective moments from your sessions appear here when you write substantively — not every visit creates an entry. Keep going; your arc builds from your own words."
          />
        )}

        <ResourceShelfSection
          title="Generated for you"
          subtitle="Not a library. A record of what you needed, and when."
          moreLink={{ to: "/resources", label: "Open resource shelf" }}
          resources={generatedStripe}
          favoriteIds={favoriteIds}
          onFavoriteClick={toggleResourceFavorite}
        />
      </Page.Content>
    </Page.Container>
  );
};

// ------------------------------------------------------------------
// Hero — the "Your Arc" framing block with stats strip.
// ------------------------------------------------------------------

const InsightsEmptyCard = ({ title, body }: { title: string; body: string }) => (
  <ContentCard className="p-6 md:p-8">
    <SectionHeading title={title} subtitle={body} />
  </ContentCard>
);

const ArcHeroCard = ({
  firstName,
  arrivedLabel,
  sessionsCount,
  milestonesCount,
  hasPersonalData,
}: {
  firstName: string;
  arrivedLabel: string;
  sessionsCount: number;
  milestonesCount: number;
  hasPersonalData: boolean;
}) => {
  return (
    <ContentCard className="p-6 md:p-10">
      <div className="flex items-center gap-2 font-inter text-[11px] font-semibold uppercase tracking-[0.18em] text-bodhi-orange md:text-xs">
        <Sparkle className="h-3.5 w-3.5 text-bodhi-gold" aria-hidden />
        <span>Your Arc · A Mirror, Not a Dashboard</span>
      </div>

      <h2 className="mt-3 font-literata text-2xl font-semibold leading-tight text-stone-900 md:mt-4 md:text-[2rem] md:leading-[1.2]">
        This is your <em className="italic text-bodhi-teal">story</em>, {firstName} — the shape of who you are
        becoming in this work.
      </h2>

      <p className="mt-4 max-w-3xl font-inter text-sm italic leading-relaxed text-stone-500 md:text-base">
        Everything here came from your own sessions. Nothing was assigned to you. Nothing was scored by anyone
        else. What follows is only the record of your own words, and what they have quietly been telling you.
      </p>

      <div className="mt-6 border-t border-stone-200 pt-5 md:mt-8 md:pt-6">
        <dl className="grid grid-cols-2 gap-x-8 gap-y-5 md:grid-cols-4">
          <StatBlock label="Arrived" value={arrivedLabel} />
          <StatBlock label="Sessions" value={sessionsCount.toString()} />
          <StatBlock label="Milestones" value={milestonesCount.toString()} />
          <StatBlock
            label="Your trajectory"
            value={
              hasPersonalData ? (
                <span className="inline-flex items-center gap-1.5 text-bodhi-teal">
                  <ArrowUpRight className="h-4 w-4 md:h-5 md:w-5" aria-hidden />
                  <span className="font-literata font-semibold">building</span>
                </span>
              ) : (
                <span className="font-literata font-semibold text-stone-400">—</span>
              )
            }
          />
        </dl>
      </div>
    </ContentCard>
  );
};

const StatBlock = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <dt className="font-inter text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400 md:text-[11px]">
      {label}
    </dt>
    <dd className="mt-1 font-literata text-lg font-semibold text-stone-900 md:text-2xl">{value}</dd>
  </div>
);

const GROWTH_PATTERN_ACCENTS: Record<
  GrowthPatternAccent,
  { border: string; segment: string; category: string }
> = {
  emerald: {
    border: "bg-emerald-800",
    segment: "bg-emerald-800",
    category: "text-emerald-800",
  },
  teal: {
    border: "bg-bodhi-teal",
    segment: "bg-bodhi-teal",
    category: "text-bodhi-teal",
  },
  gold: {
    border: "bg-bodhi-gold",
    segment: "bg-bodhi-gold",
    category: "text-amber-900",
  },
  violet: {
    border: "bg-violet-700",
    segment: "bg-violet-700",
    category: "text-violet-800",
  },
};

// ------------------------------------------------------------------
// How you are growing — session mirror + four patterned cards (segmented progress).
// ------------------------------------------------------------------
const HowYouAreGrowingSection = ({
  userId,
  mirrorQueue,
  patterns,
  onMirrorFeedbackRecorded,
}: {
  userId: string;
  mirrorQueue: SessionMirrorPrompt[];
  patterns: GrowthPattern[];
  onMirrorFeedbackRecorded?: () => void;
}) => {
  const [responsesTick, setResponsesTick] = useState(0);

  const answeredIds = useMemo(() => answeredInsightIds(userId), [userId, responsesTick]);

  const currentMirror = useMemo(() => {
    return mirrorQueue.find((m) => !answeredIds.has(m.id)) ?? null;
  }, [mirrorQueue, answeredIds]);

  const handleMirrorFeedback = (feedback: MirrorInsightFeedback) => {
    if (!currentMirror) return;
    recordMirrorInsightResponse(userId, currentMirror.id, feedback);
    onMirrorFeedbackRecorded?.();
    const nextAnswered = answeredInsightIds(userId);
    const hasAnother = mirrorQueue.some((m) => !nextAnswered.has(m.id));
    setResponsesTick((t) => t + 1);
    toast.success("Saved — thanks for letting HeyBodhi know.", {
      description: hasAnother ? "Here's another reflection when you're ready." : undefined,
    });
  };

  const mirrorPrimaryButtonClass =
    "rounded-full px-4 py-2 font-inter text-sm transition md:px-5 bg-emerald-800 text-white shadow-sm hover:bg-emerald-900";
  const mirrorOutlineButtonClass =
    "rounded-full border border-stone-200 bg-white px-4 py-2 font-inter text-sm text-stone-800 transition hover:border-stone-300 hover:bg-stone-50 md:px-5";

  return (
    <ContentCard className="p-6 md:p-8">
      <div className="border-b border-stone-200 pb-6 md:pb-8">
        {currentMirror ? (
          <div aria-live="polite">
            <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400 md:text-[11px]">
              {currentMirror.eyebrow}
            </p>
            <blockquote className="mt-3 font-literata text-lg font-medium italic leading-snug text-stone-900 md:text-xl md:leading-relaxed">
              &ldquo;{currentMirror.quote}&rdquo;
            </blockquote>
            <div className="mt-5 flex flex-wrap justify-end gap-2 md:gap-2.5">
              <button type="button" onClick={() => handleMirrorFeedback("resonates")} className={mirrorPrimaryButtonClass}>
                This resonates
              </button>
              <button type="button" onClick={() => handleMirrorFeedback("not_quite")} className={mirrorOutlineButtonClass}>
                Not quite
              </button>
              <button type="button" onClick={() => handleMirrorFeedback("sitting")} className={mirrorOutlineButtonClass}>
                Still sitting with this
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-stone-100 bg-stone-50/90 px-4 py-5 text-center md:px-6">
            <p className="font-inter text-sm leading-relaxed text-stone-600">
              You&apos;re caught up on reflections from HeyBodhi for now. When there&apos;s something new from your
              sessions, it&apos;ll show up here.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 md:mt-8">
        <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-400 md:text-[11px]">
          How you are growing
        </p>
        <h2 className="mt-2 font-literata text-xl font-semibold leading-tight text-stone-900 md:text-2xl">
          Four quiet patterns, changing slowly
        </h2>
        <p className="mt-2 max-w-3xl font-inter text-sm italic leading-relaxed text-stone-500 md:text-base">
          These only shift when you&apos;ve confirmed they&apos;re true. A hard week doesn&apos;t move them backward.
        </p>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 md:mt-6 md:grid-cols-2 md:gap-5">
        {patterns.length > 0 ? (
          patterns.map((pattern) => (
            <GrowthPatternCard key={pattern.id} pattern={pattern} />
          ))
        ) : (
          <div className="md:col-span-2 rounded-xl border border-stone-100 bg-stone-50/90 px-4 py-5 text-center">
            <p className="font-inter text-sm leading-relaxed text-stone-600">
              Growth patterns appear here once HeyBodhi has enough from your sessions to reflect back —
              nothing is pre-filled.
            </p>
          </div>
        )}
      </div>
    </ContentCard>
  );
};

const SegmentedProgressBar = ({
  filled,
  segmentClass,
}: {
  filled: number;
  segmentClass: string;
}) => {
  const n = Math.max(0, Math.min(4, filled));
  return (
    <div
      className="flex gap-1"
      role="img"
      aria-label={`Progress: ${n} of 4 stages`}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            "h-2 flex-1 rounded-sm",
            i < n ? segmentClass : "bg-stone-200"
          )}
        />
      ))}
    </div>
  );
};

const GrowthPatternCard = ({ pattern }: { pattern: GrowthPattern }) => {
  const accent = GROWTH_PATTERN_ACCENTS[pattern.accent];
  const filled = Math.max(0, Math.min(4, Math.round(pattern.segmentsFilled)));

  return (
    <div className="relative overflow-hidden rounded-2xl border border-stone-200 bg-white p-5 shadow-sm md:p-6">
      <span
        aria-hidden
        className={cn("absolute inset-x-0 top-0 h-1.5 rounded-t-2xl", accent.border)}
      />
      <p
        className={cn(
          "font-inter text-[10px] font-semibold uppercase tracking-[0.18em] md:text-[11px]",
          accent.category
        )}
      >
        {pattern.categoryLabel}
      </p>
      <p className="mt-2 font-literata text-base font-semibold leading-snug text-stone-900 md:text-lg">
        {pattern.insightTitle}
      </p>
      <div className="mt-4">
        <SegmentedProgressBar filled={filled} segmentClass={accent.segment} />
      </div>
      <p className="mt-3 font-inter text-xs leading-relaxed text-stone-500 md:text-sm">
        {pattern.footerNote}
      </p>
    </div>
  );
};

// ------------------------------------------------------------------
// Language shifts — before/after quote pairs from the user's own writing.
// ------------------------------------------------------------------
const LanguageShiftsCard = ({ shifts }: { shifts: LanguageShift[] }) => {
  return (
    <ContentCard className="p-6 md:p-8">
      <SectionHeading
        title="How your language is shifting"
        subtitle="The most honest signal of change — the words you reach for when no one is watching."
      />
      <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5 md:mt-6 md:p-6">
        <ul className="flex flex-col divide-y divide-stone-200">
          {shifts.map((shift) => (
            <li
              key={shift.id}
              className="grid grid-cols-1 items-center gap-2 py-4 first:pt-0 last:pb-0 md:grid-cols-[1fr_auto_1fr] md:gap-6"
            >
              <p className="font-inter text-sm italic text-stone-400 line-through md:text-right md:text-base">
                &ldquo;{shift.before}&rdquo;
              </p>
              <span aria-hidden className="font-inter text-bodhi-teal md:px-2">
                →
              </span>
              <p className="font-literata text-sm italic font-medium text-stone-800 md:text-base">
                &ldquo;{shift.after}&rdquo;
              </p>
            </li>
          ))}
        </ul>
      </div>
    </ContentCard>
  );
};

// ------------------------------------------------------------------
// Growth narrative — timeline of reflections / practice loops / milestones.
// ------------------------------------------------------------------
const GrowthNarrativeCard = ({ entries }: { entries: NarrativeEntry[] }) => {
  const [activeFilter, setActiveFilter] = useState<NarrativeKind | "all">("all");

  const filteredEntries = useMemo(
    () =>
      activeFilter === "all" ? entries : entries.filter((e) => e.kind === activeFilter),
    [entries, activeFilter]
  );

  return (
    <section>
      <SectionHeading
        title="Your growth narrative"
        subtitle="Moments drawn from your reflective writing — not a log of every session."
        tight
      />
      <ContentCard className="mt-4 p-0 md:mt-5">
        <div className="flex items-center justify-between gap-3 border-b border-stone-200 px-4 py-3 md:px-6 md:py-4">
          <span className="font-inter text-xs font-medium uppercase tracking-wider text-stone-400 md:text-sm">
            Filter
          </span>
          <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
            {NARRATIVE_FILTERS.map((filter) => {
              const isActive = activeFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => setActiveFilter(filter.id)}
                  className={cn(
                    "rounded-full border px-3 py-1 font-inter text-xs transition md:text-sm",
                    isActive
                      ? "border-transparent bg-bodhi-teal text-white"
                      : "border-stone-200 bg-white text-stone-600 hover:border-stone-300 hover:text-stone-800"
                  )}
                  aria-pressed={isActive}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
        </div>

        <ul className="px-4 py-5 md:px-8 md:py-8">
          {filteredEntries.map((entry, index) => (
            <NarrativeTimelineItem
              key={entry.id}
              entry={entry}
              isLast={index === filteredEntries.length - 1}
            />
          ))}
          {filteredEntries.length === 0 && (
            <p className="py-6 text-center font-inter text-sm text-stone-400">
              No entries of that kind yet — keep going.
            </p>
          )}
        </ul>
      </ContentCard>
    </section>
  );
};

const KIND_META: Record<
  NarrativeKind,
  { label: string; dotClass: string }
> = {
  milestone: { label: "milestone", dotClass: "bg-bodhi-teal ring-2 ring-bodhi-teal/20" },
  practice: { label: "practice", dotClass: "bg-bodhi-teal-light ring-2 ring-bodhi-teal-light/25" },
  reflection: { label: "reflection", dotClass: "bg-bodhi-gold ring-2 ring-bodhi-gold/25" },
};

const NarrativeTimelineItem = ({ entry, isLast }: { entry: NarrativeEntry; isLast: boolean }) => {
  const meta = KIND_META[entry.kind];

  return (
    <li className="relative grid grid-cols-[20px_1fr] gap-x-4 pb-6 last:pb-0 md:gap-x-5">
      <div className="relative flex justify-center">
        <span
          className={cn(
            "mt-1.5 h-3 w-3 shrink-0 rounded-full",
            entry.isStart ? "bg-white ring-2 ring-stone-300" : meta.dotClass
          )}
          aria-hidden
        />
        {!isLast && (
          <span aria-hidden className="absolute top-5 h-full w-px bg-stone-200" />
        )}
      </div>

      <div className="min-w-0">
        <p className="font-inter text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-400 md:text-[11px]">
          {entry.when} · {entry.isStart ? "Where you began" : meta.label}
        </p>
        <blockquote
          className={cn(
            "mt-1 font-literata italic text-stone-900",
            entry.kind === "milestone" || entry.isStart
              ? "text-base font-semibold md:text-lg"
              : "text-sm font-medium md:text-[15px]"
          )}
        >
          &ldquo;{entry.quote}&rdquo;
        </blockquote>
        {entry.caption && (
          <p className="mt-1.5 font-inter text-xs italic text-stone-500 md:text-sm">{entry.caption}</p>
        )}
        {entry.tag && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <NarrativePill kind={entry.kind} tone={entry.tone} label={entry.tag} />
          </div>
        )}
        {entry.kind === "milestone" && !entry.tag && (
          <div className="mt-2">
            <NarrativePill kind="milestone" label="+ milestone" />
          </div>
        )}
        {entry.isStart && !entry.tag && (
          <div className="mt-2">
            <NarrativePill kind="reflection" label="Your why" />
          </div>
        )}
      </div>
    </li>
  );
};

const NarrativePill = ({
  kind,
  tone,
  label,
}: {
  kind: NarrativeKind;
  tone?: NarrativeEntry["tone"];
  label: string;
}) => {
  const isMilestone = kind === "milestone";

  let className =
    "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 font-inter text-[10px] font-semibold uppercase tracking-wider md:text-[11px]";

  if (isMilestone) {
    className += " border-transparent bg-bodhi-teal text-white";
  } else if (tone === "purposive") {
    className += " border-bodhi-gold/30 bg-bodhi-gold/10 text-bodhi-gold";
  } else if (tone === "relational") {
    className += " border-bodhi-teal/25 bg-bodhi-teal/5 text-bodhi-teal";
  } else if (kind === "practice") {
    className += " border-bodhi-teal/25 bg-bodhi-teal/10 text-bodhi-teal";
  } else {
    className += " border-stone-200 bg-stone-100 text-stone-600";
  }

  return <span className={className}>{label}</span>;
};

// ------------------------------------------------------------------
// Shared section heading (used outside ContentCards so it reads as page structure).
// ------------------------------------------------------------------
const SectionHeading = ({
  title,
  subtitle,
  tight,
}: {
  title: string;
  subtitle: string;
  tight?: boolean;
}) => (
  <div className={cn("flex flex-col gap-0.5", tight ? "px-1" : "")}>
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <h2
        className={cn(
          "font-literata font-semibold text-stone-900",
          tight ? "text-lg md:text-xl text-white" : "text-lg md:text-xl"
        )}
      >
        {title}
      </h2>
      <p
        className={cn(
          "font-inter text-xs italic md:text-sm",
          tight ? "text-white/75" : "text-stone-400"
        )}
      >
        {subtitle}
      </p>
    </div>
  </div>
);
